import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Link, Navigate } from 'react-router-dom';
import Pagination from '../components/UI/Pagination';
import { backendUrl } from '../config/api';
import { useAuth } from '../context/AuthContext';

axios.defaults.withCredentials = true;

const C = {
    card:   'rgba(13,19,50,0.65)',
    border: 'rgba(255,255,255,0.08)',
    cyan:   '#00d4ff',
    green:  '#00c853',
    amber:  '#ffb300',
    red:    '#ff3b30',
};

const fmtDate = d => {
    try {
        const x = new Date(d);
        return `${x.toLocaleDateString()} ${x.toLocaleTimeString()}`;
    } catch { return '–'; }
};
const fmtPrice = pricing => `${Number(pricing?.totalPrice ?? 0)} ${(pricing?.currency || 'credits') === 'credits' ? 'cr' : pricing?.currency}`;

const TABS = [
    { id: 'completed', label: 'Completed' },
    { id: 'running',   label: 'In Progress' },
];

export default function Results() {
    const { isLoggedIn, isAdmin, loading: authLoading } = useAuth();

    const [activeTab,  setActiveTab]  = useState('completed');
    const [games,      setGames]      = useState([]);
    const [loading,    setLoading]    = useState(true);
    const [error,      setError]      = useState(null);
    const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0, limit: 10 });

    const historyEndpoint = isAdmin
        ? `${backendUrl}/api/history/all`
        : `${backendUrl}/api/history/my`;

    const fetchGames = useCallback(async (tab = activeTab, page = 1, limit = pagination.limit) => {
        if (!isLoggedIn) return;
        setLoading(true); setError(null);
        try {
            const params = { page, limit };
            if (tab === 'completed') params.completed = true;
            if (tab === 'running')   params.completed = false;

            const res = await axios.get(historyEndpoint, { params });
            const data = res.data.data ?? [];
            const pag  = res.data.pagination ?? { total: data.length, page, limit, pages: 1 };
            setGames(data);
            setPagination({ page: pag.page, pages: pag.pages, total: pag.total, limit: pag.limit });
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load results');
        } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, pagination.limit, isLoggedIn, isAdmin]);

    useEffect(() => {
        if (authLoading) return;
        if (isLoggedIn) fetchGames(activeTab, 1, pagination.limit);
        else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authLoading, isLoggedIn, activeTab]);

    if (authLoading) return null;
    if (!isLoggedIn) return <Navigate to="/auth/login" replace />;

    const switchTab = tab => {
        setActiveTab(tab);
        setGames([]);
        setPagination({ page: 1, pages: 1, total: 0, limit: pagination.limit });
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="pt-24 pb-24 md:pb-8 px-4 md:pl-20"
            style={{ maxWidth: 1100, marginLeft: 'auto', marginRight: 'auto' }}
        >
            {/* Header */}
            <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                <div>
                    <h1 style={{ color: '#fff', fontSize: 24, fontWeight: 800, margin: 0 }}>📊 Results</h1>
                    <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13, margin: '5px 0 0' }}>
                        {isAdmin ? 'All game results globally' : 'Your game results'} — {pagination.total} total
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button
                        onClick={() => fetchGames(activeTab, pagination.page, pagination.limit)}
                        style={{ padding: '7px 14px', borderRadius: 8, fontSize: 12, background: 'rgba(255,255,255,0.06)', border: `1px solid ${C.border}`, color: 'rgba(255,255,255,0.7)', cursor: 'pointer' }}
                    >↻ Refresh</button>
                    <Link to="/" style={{ padding: '7px 14px', borderRadius: 8, fontSize: 12, background: `rgba(0,212,255,0.1)`, border: `1px solid rgba(0,212,255,0.3)`, color: C.cyan, textDecoration: 'none', fontWeight: 600 }}>
                        ← Home
                    </Link>
                </div>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: 'rgba(0,0,0,0.2)', borderRadius: 12, padding: 4, width: 'fit-content' }}>
                {TABS.map(t => (
                    <button
                        key={t.id}
                        onClick={() => switchTab(t.id)}
                        style={{
                            padding: '8px 20px', borderRadius: 10, fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer',
                            background: activeTab === t.id ? `rgba(0,212,255,0.15)` : 'transparent',
                            color: activeTab === t.id ? C.cyan : 'rgba(255,255,255,0.4)',
                            transition: 'all 0.18s',
                        }}
                    >{t.label}</button>
                ))}
            </div>

            {error && (
                <div style={{ padding: '12px 18px', borderRadius: 12, background: 'rgba(255,59,48,0.1)', border: '1px solid rgba(255,59,48,0.3)', color: C.red, marginBottom: 18, fontSize: 13 }}>
                    ⚠ {error}
                </div>
            )}

            <div style={{ background: C.card, backdropFilter: 'blur(14px)', border: `1px solid ${C.border}`, borderRadius: 18, overflow: 'hidden' }}>
                {loading ? (
                    <div style={{ padding: '80px 0', textAlign: 'center', color: 'rgba(255,255,255,0.4)' }}>
                        <div style={{ width: 36, height: 36, border: '2px solid rgba(0,212,255,0.2)', borderTop: `2px solid ${C.cyan}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
                        Loading results…
                    </div>
                ) : games.length === 0 ? (
                    <div style={{ padding: '80px 0', textAlign: 'center' }}>
                        <div style={{ fontSize: 40, opacity: 0.25, marginBottom: 14 }}>🎮</div>
                        <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 14 }}>
                            {activeTab === 'completed' ? 'No completed games found' : 'No games currently in progress'}
                        </div>
                    </div>
                ) : (
                    <>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                <thead>
                                    <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                                        {['Room', 'Players', 'Password', 'Price', activeTab === 'completed' ? 'Winner' : 'Status', 'Date'].map(h => (
                                            <th key={h} style={{
                                                padding: '12px 16px', textAlign: 'left',
                                                fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.3)',
                                                textTransform: 'uppercase', letterSpacing: 1,
                                                background: 'rgba(0,0,0,0.1)', whiteSpace: 'nowrap',
                                            }}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {games.map((g, idx) => (
                                        <tr
                                            key={g._id || g.roomId}
                                            style={{
                                                borderBottom: `1px solid rgba(255,255,255,0.04)`,
                                                background: idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)',
                                                transition: 'background 0.15s',
                                            }}
                                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,212,255,0.03)'}
                                            onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)'}
                                        >
                                            <td style={{ padding: '11px 16px' }}>
                                                {g.roomCode && (
                                                    <div style={{ fontFamily: 'monospace', fontSize: 12, color: C.amber, fontWeight: 700, marginBottom: 2 }}>
                                                        {g.roomCode}
                                                    </div>
                                                )}
                                                <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.28)', fontFamily: 'monospace' }}>{g.roomId}</div>
                                            </td>
                                            <td style={{ padding: '11px 16px', fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>
                                                {Array.isArray(g.players) && g.players.length > 0
                                                    ? g.players.map(p => p.name || p).filter(Boolean).join(', ')
                                                    : '—'}
                                            </td>
                                            <td style={{ padding: '11px 16px' }}>
                                                {g.password
                                                    ? <span style={{ fontSize: 11, color: 'rgba(255,179,0,0.8)', fontWeight: 600 }}>🔒 Password set</span>
                                                    : <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', fontWeight: 600 }}>None</span>
                                                }
                                            </td>
                                            <td style={{ padding: '11px 16px', fontSize: 11, color: 'rgba(255,255,255,0.8)', whiteSpace: 'nowrap' }}>
                                                {fmtPrice(g.pricing)}
                                            </td>
                                            <td style={{ padding: '11px 16px', fontSize: 11 }}>
                                                {activeTab === 'completed'
                                                    ? (g.winner
                                                        ? <span style={{ color: C.amber, fontWeight: 700 }}>🏆 {g.winner}</span>
                                                        : <span style={{ color: 'rgba(255,255,255,0.2)' }}>—</span>)
                                                    : <span style={{ padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: 'rgba(0,212,255,0.12)', border: '1px solid rgba(0,212,255,0.3)', color: C.cyan }}>Live</span>
                                                }
                                            </td>
                                            <td style={{ padding: '11px 16px', fontSize: 11, color: 'rgba(255,255,255,0.4)', whiteSpace: 'nowrap' }}>
                                                {fmtDate(g.createdAt || g.date)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div style={{ padding: '12px 20px', borderTop: `1px solid ${C.border}` }}>
                            <Pagination
                                page={pagination.page}
                                pages={pagination.pages}
                                total={pagination.total}
                                limit={pagination.limit}
                                onPage={p => fetchGames(activeTab, p, pagination.limit)}
                                onLimit={l => { setPagination(prev => ({ ...prev, limit: l })); fetchGames(activeTab, 1, l); }}
                            />
                        </div>
                    </>
                )}
            </div>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </motion.div>
    );
}
